import { useState, useCallback } from 'react';
import axios from 'axios';
import { reelService } from 'api/services/ReelService';
import type { ReelViewModel } from 'api/types/reel';

export interface CreateReelResponse {
  isSuccess: boolean;
  createdReel: ReelViewModel | null;
}

export interface UseCreateReelResult {
  createReel: (file: File) => Promise<CreateReelResponse>;
  loading: boolean;
  error: string | null;
  resetError: () => void;
}

function normalizeReelItem(raw: unknown): ReelViewModel {
  const o = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const listingIdRaw = o.listingId ?? o.ListingId;
  return {
    id: String(o.id ?? o.Id ?? ''),
    videoUrl: String(o.videoUrl ?? o.VideoUrl ?? ''),
    userId: String(o.userId ?? o.UserId ?? ''),
    createdAt: (() => {
      const v = o.createdAt ?? o.CreatedAt ?? o.createdAtUtc ?? o.CreatedAtUtc;
      return typeof v === 'string' ? v : null;
    })(),
    listingId: typeof listingIdRaw === 'string' && listingIdRaw.length > 0 ? listingIdRaw : null,
  };
}

/**
 * Pull a Turkish backend error message from an Axios/network error, preferring
 * the ServiceResult envelope's `message` over generic transport errors (feature 009, FR-019).
 */
function extractErrorMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as { message?: string; Message?: string } | undefined;
    const serverMessage = data?.message ?? data?.Message;
    if (typeof serverMessage === 'string' && serverMessage.trim().length > 0) {
      return serverMessage;
    }
    if (err.message) return err.message;
  }
  if (err instanceof Error && err.message) return err.message;
  return 'Bir hata oluştu.';
}

function extractCreatedReel(rawResult: unknown): ReelViewModel | null {
  const resultObj =
    rawResult && typeof rawResult === 'object' ? (rawResult as Record<string, unknown>) : null;
  if (!resultObj) return null;

  const candidates: unknown[] = [
    resultObj.data,
    resultObj.Data,
    (resultObj.data as Record<string, unknown> | undefined)?.data,
    (resultObj.data as Record<string, unknown> | undefined)?.Data,
    (resultObj.Data as Record<string, unknown> | undefined)?.data,
    (resultObj.Data as Record<string, unknown> | undefined)?.Data,
  ];

  for (const candidate of candidates) {
    if (!candidate) continue;
    const normalized = normalizeReelItem(candidate);
    if (normalized.id && normalized.videoUrl) {
      return normalized;
    }
  }

  return null;
}

/**
 * Upload a reel via POST /api/reels (multipart). Does not fetch blob – backend returns ReelViewModel with videoUrl.
 */
export function useCreateReel(): UseCreateReelResult {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createReel = useCallback(async (file: File): Promise<CreateReelResponse> => {
    setLoading(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('Video', file);
      const result = await reelService.create(formData);
      if (result.isSuccess) {
        const createdReel = extractCreatedReel(result);
        return {
          isSuccess: true,
          createdReel,
        };
      }
      setError(result.message ?? 'Yükleme başarısız.');
      return {
        isSuccess: false,
        createdReel: null,
      };
    } catch (err) {
      setError(extractErrorMessage(err));
      return {
        isSuccess: false,
        createdReel: null,
      };
    } finally {
      setLoading(false);
    }
  }, []);

  const resetError = useCallback(() => setError(null), []);

  return { createReel, loading, error, resetError };
}
